/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  reatomArray,
  reatomBoolean,
  withAssign,
} from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'
import * as DateF from 'date-fns'
import { nanoid } from 'nanoid'

import { selectedNetworkAtom, type Network } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms'
import { poolActivityEvent } from '@root/entities/realtime'
import { environmentAtom, STORAGE_PREFIX } from '@root/shared'
import { reatomFactoryCache } from '@root/shared/lib'

import { PairActivity } from '../model'

const SHOW_DELAY_MS = DateF.secondsToMilliseconds(5)

type Event = {
  id: string
  activity: PairActivity.Activity
}

const STORAGE_KEY = `${STORAGE_PREFIX}/v1/showPairActivityNotifications`

export const showPairActivityNotificationsAtom = reatomBoolean(false).pipe(
  withLocalStorage(STORAGE_KEY),
)

const reatomPairActivity_ = ({ network }: { network: Network.Network }) => {
  const notificationsAtom = reatomArray<Event>([], 'notificationsAtom')
  const timers = new Map<string, number>()

  poolActivityEvent.onCall((ctx, payload) => {
    const showPoolActivityNotifications = ctx.get(
      showPairActivityNotificationsAtom,
    )
    if (!showPoolActivityNotifications) {
      return
    }

    const {
      assets: { tokenLogosHost, avatarsHost },
    } = ctx.get(environmentAtom)

    const id = nanoid()
    const pair =
      'pairInfo' in payload && payload.pairInfo
        ? pairRegistry.resolveFromDto(ctx, payload.pairInfo)
        : undefined
    const activity = PairActivity.fromDto(
      payload,
      pair,
      tokenLogosHost,
      avatarsHost,
    )

    notificationsAtom(ctx, (notifications) => {
      // Only 1 notification per type
      const filtered = notifications.filter(
        (notification) => notification.activity.type !== activity.type,
      )
      return [...filtered, { id, activity }]
    })

    timers.set(
      id,
      window.setTimeout(() => {
        closeAction(ctx, id)
      }, SHOW_DELAY_MS),
    )
  })

  const closeAction = action((ctx, id: string) => {
    notificationsAtom(ctx, (events) =>
      events.filter((event) => event.id !== id),
    )
    clearTimeout(timers.get(id))
    timers.delete(id)
  }, 'closeAction')

  return notificationsAtom.pipe(
    withAssign(() => ({ network, close: closeAction })),
  )
}
const reatomPairActivity = reatomFactoryCache({
  factory: reatomPairActivity_,
  keyFn: ({ network }) => network,
  clearOnDisconnect: false,
})

export const pairActivityModelAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const model = reatomPairActivity({ network })
  return model
})
export const pairActivityNotificationsAtom = atom((ctx) => {
  const model = ctx.spy(pairActivityModelAtom)
  return ctx.spy(model)
})
